import { useEffect, useMemo, useState } from 'react';
import {
    Control,
    Controller,
    FieldError,
    FieldValues,
    Path,
    UseFormClearErrors,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import container from 'src/container';
import {
    OrganizationGroup,
    OrganizationHierarchyLevel,
} from 'src/domain/organization';
import { useNotification } from 'src/components/Notification/useNotfication';
import { getAvailableLeafGroups } from 'src/utils/roomAvailability';
import { Label, Error as InputError } from './Input';

const {
    cradle: { munduClient },
} = container;

type LeftGroupSelectProps<TFieldValues extends FieldValues> = {
    control: Control<TFieldValues>;
    name: Path<TFieldValues>;
    organizationId?: string;
    error?: FieldError;
    disabled?: boolean;
    clearErrors?: UseFormClearErrors<TFieldValues>;
    label?: string;
    allowedGroupIds?: string[];
    excludedGroupIds?: string[];
    emptyStateVariant?: 'default' | 'available';
};

const getGroupLabel = (group?: OrganizationGroup) => {
    if (!group) return '';
    return group.name?.trim().length ? group.name : group.id;
};

const replaceGroupWords = (value: string, replacement: string) => {
    return value
        .replace(/subgroups?/gi, replacement)
        .replace(/groups?/gi, replacement)
        .replace(/undergrupper?/gi, replacement)
        .replace(/grupper?/gi, replacement);
};

export const LeftGroupSelect = <TFieldValues extends FieldValues>({
    control,
    name,
    organizationId,
    error,
    disabled = false,
    clearErrors,
    label,
    allowedGroupIds,
    excludedGroupIds,
    emptyStateVariant = 'default',
}: LeftGroupSelectProps<TFieldValues>) => {
    const { t } = useTranslation();
    const notify = useNotification();
    const [organizationGroups, setOrganizationGroups] = useState<
        OrganizationGroup[]
    >([]);
    const [organizationHierarchy, setOrganizationHierarchy] = useState<
        OrganizationHierarchyLevel[]
    >([]);
    const [isGroupsLoading, setIsGroupsLoading] = useState(false);

    useEffect(() => {
        if (!organizationId) {
            setOrganizationGroups([]);
            setOrganizationHierarchy([]);
            return;
        }

        let isMounted = true;
        const fetchGroups = async () => {
            try {
                setIsGroupsLoading(true);
                const organization = await munduClient.getOrganization(
                    organizationId,
                );
                if (isMounted) {
                    setOrganizationGroups(organization.groups ?? []);
                    setOrganizationHierarchy(organization.hierarchy ?? []);
                }
            } catch (error_) {
                if (error_ instanceof Error) {
                    notify(error_.message, true);
                }
            } finally {
                if (isMounted) {
                    setIsGroupsLoading(false);
                }
            }
        };

        fetchGroups();

        return () => {
            isMounted = false;
        };
    }, [organizationId, notify]);

    const leafGroups = useMemo(() => {
        return getAvailableLeafGroups({
            organizationGroups,
            organizationHierarchy,
            allowedGroupIds,
            excludedGroupIds,
        });
    }, [
        allowedGroupIds,
        excludedGroupIds,
        organizationGroups,
        organizationHierarchy,
    ]);

    const lowestHierarchyLabel = useMemo(() => {
        if (!organizationHierarchy.length) {
            return undefined;
        }

        const byParent = new Map<string, OrganizationHierarchyLevel[]>();
        for (const level of organizationHierarchy) {
            const parentKey = level.parentId ?? '';
            const current = byParent.get(parentKey) ?? [];
            current.push(level);
            byParent.set(parentKey, current);
        }

        const roots = byParent.get('') ?? [];
        if (!roots.length) {
            return undefined;
        }

        let maxDepth = -1;
        const namesAtMaxDepth = new Set<string>();

        const visit = (
            current: OrganizationHierarchyLevel,
            depth: number,
            visited: Set<string>,
        ) => {
            if (visited.has(current.id)) {
                return;
            }

            const nextVisited = new Set(visited);
            nextVisited.add(current.id);

            if (depth > maxDepth) {
                maxDepth = depth;
                namesAtMaxDepth.clear();
                namesAtMaxDepth.add(current.name);
            } else if (depth === maxDepth) {
                namesAtMaxDepth.add(current.name);
            }

            const children = byParent.get(current.id) ?? [];
            children.forEach(child => visit(child, depth + 1, nextVisited));
        };

        roots.forEach(root => visit(root, 0, new Set()));

        const labels = [...namesAtMaxDepth]
            .map(name => name?.trim())
            .filter(Boolean)
            .sort((a, b) =>
                a.localeCompare(b, undefined, { sensitivity: 'base' }),
            );

        return labels[0];
    }, [organizationHierarchy]);

    const hasLeafGroups = leafGroups.length > 0;
    const resolvedLabel =
        label ?? lowestHierarchyLabel ?? t('users.form.groupsLabel');
    const basePlaceholder = t('users.form.groupsChildPlaceholder');
    const resolvedPlaceholder = lowestHierarchyLabel
        ? replaceGroupWords(basePlaceholder, lowestHierarchyLabel)
        : basePlaceholder;
    const emptyMessage =
        emptyStateVariant === 'available' && lowestHierarchyLabel
            ? t('users.form.groupsAvailableEmpty', {
                  levelName: lowestHierarchyLabel,
              })
            : t('users.form.groupsEmpty');

    return (
        <div className="space-y-2">
            <Label label={resolvedLabel} className="mb-0" />
            {isGroupsLoading && (
                <p className="text-sm text-mongooseGray">
                    {t('common.loading')}
                </p>
            )}
            {!isGroupsLoading && !hasLeafGroups ? (
                <p className="text-sm text-mongooseGray">{emptyMessage}</p>
            ) : null}

            <Controller
                control={control}
                name={name}
                render={({ field }) => {
                    const fieldValue = field.value as string[] | undefined;
                    const selectedLeafId = fieldValue?.find(Boolean) ?? '';

                    return (
                        <select
                            className="text-input"
                            value={selectedLeafId}
                            onChange={event => {
                                const nextValue = event.target.value;
                                field.onChange(nextValue ? [nextValue] : []);
                                clearErrors?.(name);
                            }}
                            disabled={
                                disabled || isGroupsLoading || !hasLeafGroups
                            }
                        >
                            <option value="">{resolvedPlaceholder}</option>
                            {leafGroups.map(option => (
                                <option key={option.id} value={option.id}>
                                    {getGroupLabel(option)}
                                </option>
                            ))}
                        </select>
                    );
                }}
            />

            <InputError error={error} />
        </div>
    );
};
